import { useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/redux/Store';
import { preparePostSpotlight, executePostSpotlightEmailUser, recordPostSpotlight } from '@/redux/thunks/postThunks';
import { TransactionDetails } from '../components/TransactionConfirmModal';
import { buildSignBroadcast } from '@/utils/kleverTx';

interface PrepareResponse {
  payload: any;
  metadata: string;
  kdaFee?: string;
  network?: string;
  pricing: {
    pricePerHour: string;
    durationHours: number;
    totalCost: string;
    estimatedGas: string;
    totalTransaction: string;
  };
  post: {
    id: string;
    profileId: number;
    locationId: string;
  };
  canProceed?: boolean;
  warning?: string;
  balance?: {
    current: number;
    required: string;
    shortfall: string | null;
  };
}

interface PurchaseParams {
  postId: string;
  durationHours: number;
}

export const usePostSpotlight = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [transactionDetails, setTransactionDetails] = useState<TransactionDetails | null>(null);
  const [preparedData, setPreparedData] = useState<PrepareResponse | null>(null);

  /**
   * Step 1: Prepare the transaction (validate and get pricing)
   */
  const prepareTransaction = async (params: PurchaseParams): Promise<TransactionDetails> => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await dispatch(preparePostSpotlight(params)).unwrap();
      const data = result as PrepareResponse;
      setPreparedData(data);

      // Build transaction details for the modal
      const details: TransactionDetails = {
        type: 'post_spotlight',
        title: 'Confirm Post Spotlight',
        description: `You are about to spotlight your post for ${data.pricing.durationHours} hours.`,
        mainCost: data.pricing.totalCost,
        mainCostLabel: `Spotlight Fee (${data.pricing.durationHours}hr × ${data.pricing.pricePerHour} KLYX)`,
        feeBreakdown: [
          {
            label: 'App pool receives',
            amount: (parseFloat(data.pricing.totalCost) * 0.95).toFixed(2),
            percentage: '95%',
          },
          {
            label: 'Profile owner receives',
            amount: (parseFloat(data.pricing.totalCost) * 0.05).toFixed(2),
            percentage: '5%',
          },
        ],
        estimatedGas: data.pricing.estimatedGas,
        totalCost: data.pricing.totalTransaction,
        confirmButtonText: 'Confirm & Spotlight',
        canProceed: data.canProceed !== false,
        warning: data.warning,
        balance: data.balance,
      };

      setTransactionDetails(details);
      return details;
    } catch (err: any) {
      const errorMsg = err || 'Failed to prepare transaction';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Step 2: Execute the transaction (sign with wallet extension)
   */
  const executeTransaction = async (): Promise<string> => {
    if (!preparedData) {
      throw new Error('Transaction not prepared');
    }

    setIsLoading(true);
    setError(null);

    try {
      const txHash = await buildSignBroadcast({
        payload: preparedData.payload,
        metadata: preparedData.metadata,
        network: preparedData.network,
        kdaFee: preparedData.kdaFee,
      });
      return txHash;
    } catch (err: any) {
      const errorMsg = err.message || 'Failed to sign transaction';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Step 3: Record the purchase in the database
   */
  const recordPurchase = async (txHash: string): Promise<void> => {
    if (!preparedData) {
      throw new Error('Transaction not prepared');
    }

    setIsLoading(true);
    setError(null);

    try {
      await dispatch(
        recordPostSpotlight({
          postId: preparedData.post.id,
          durationHours: preparedData.pricing.durationHours,
          txHash,
          locationId: preparedData.post.locationId,
        })
      ).unwrap();
    } catch (err: any) {
      const errorMsg = err || 'Failed to record purchase';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Complete flow for email users: backend handles everything
   */
  const executeEmailUserFlow = async (params: PurchaseParams): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      // Call backend to execute transaction (backend signs with custodial wallet)
      const result = await dispatch(
        executePostSpotlightEmailUser({
          postId: params.postId,
          durationHours: params.durationHours,
        })
      ).unwrap();

      const { txHash, post } = result;

      // Now record the purchase
      await dispatch(
        recordPostSpotlight({
          postId: params.postId,
          durationHours: params.durationHours,
          txHash,
          locationId: post.locationId,
        })
      ).unwrap();
    } catch (err: any) {
      // Check if error contains structured pricing data (insufficient funds)
      if (err && typeof err === 'object' && err.pricing && err.balance) {
        const detailedError = `Insufficient funds. You need ${err.balance.required} KLYX (current balance: ${err.balance.current.toFixed(2)} KLYX, shortfall: ${err.balance.shortfall} KLYX). ${err.message || ''}`;
        setError(detailedError);
        throw new Error(detailedError);
      }
      const errorMsg = err || 'Failed to complete spotlight';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Complete flow: prepare -> execute -> record
   */
  const purchaseSpotlight = async (params: PurchaseParams): Promise<void> => {
    try {
      // Check if email user
      if (user?.authMethod === 'email') {
        // Email users: backend handles everything
        await executeEmailUserFlow(params);
        return;
      }

      // Wallet users: Step 1 - Prepare
      await prepareTransaction(params);

      // Modal shows here - executeTransaction will be called when user confirms

      // Steps 2 & 3 are called separately after user confirmation
    } catch (err) {
      // Error is already set in prepareTransaction or executeEmailUserFlow
      throw err;
    }
  };

  const reset = () => {
    setIsLoading(false);
    setError(null);
    setTransactionDetails(null);
    setPreparedData(null);
  };

  return {
    isLoading,
    error,
    transactionDetails,
    prepareTransaction,
    executeTransaction,
    recordPurchase,
    purchaseSpotlight,
    reset,
  };
};

export default usePostSpotlight;
